import { useMemo, useState } from 'react';

import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui';
import AddUserDialog from '../components/users/AddUserDialog';
import { UsersTable } from '../components/users/UsersTable';
import { useCreateUser, useDeleteUser, useMe, useRoles, useUpdateUser, useUsers } from '../hooks';
import { useIntl } from '../hooks/useIntl.js';
import type { AppUser } from '../types/user.js';
import type { UserFormValues } from '../validations/user-form.js';

const EMPTY_FORM_VALUES: UserFormValues = {
  username: '',
  password: '',
  role: 'operator',
  tags: [],
};

const normalizeTagIds = (tags: UserFormValues['tags']) => {
  const seen = new Set<string>();
  const ids: string[] = [];
  tags.forEach((tag) => {
    if (tag.id && !seen.has(tag.id)) {
      seen.add(tag.id);
      ids.push(tag.id);
    }
  });
  return ids;
};

const areTagIdListsEqual = (a: string[], b: string[]) => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, index) => value === sortedB[index]);
};

function AccessDenied() {
  const intl = useIntl();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          {intl.formatMessage({ id: 'users.accessDenied.title' })}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-gray-600">
          {intl.formatMessage({ id: 'users.accessDenied.accessDeniedDesc' })}
        </p>
      </CardContent>
    </Card>
  );
}

export default function Users() {
  const intl = useIntl();
  const { data: me, isLoading: isLoadingMe } = useMe();
  const { isAdminUser } = useRoles(me);
  const {
    data: users = [],
    isLoading: isLoadingUsers,
    isRefetching,
    error,
    refetch,
  } = useUsers({ enabled: isAdminUser });
  const createUser = useCreateUser();
  const updateUser = useUpdateUser();
  const deleteUserMutation = useDeleteUser();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [selectedUser, setSelectedUser] = useState<AppUser | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AppUser | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const dialogInitialValues = useMemo<UserFormValues>(() => {
    if (selectedUser) {
      return {
        username: selectedUser.username,
        password: '',
        role: selectedUser.role,
        tags: selectedUser.tags?.map((tag) => ({ ...tag })) ?? [],
      };
    }
    return { ...EMPTY_FORM_VALUES, tags: [] };
  }, [selectedUser]);

  const resetDialog = () => {
    setIsDialogOpen(false);
    setDialogMode('create');
    setSelectedUser(null);
  };

  const handleDialogOpenChange = (open: boolean) => {
    if (open) {
      setIsDialogOpen(true);
    } else {
      resetDialog();
    }
  };

  const handleDialogSubmit = async (values: UserFormValues): Promise<void | string> => {
    const desiredTagIds = normalizeTagIds(values.tags ?? []);
    try {
      if (dialogMode === 'create') {
        await createUser.mutateAsync({
          username: values.username.trim(),
          password: values.password,
          role: values.role,
          tagIds: desiredTagIds,
        });
        resetDialog();
        return;
      }

      if (!selectedUser) {
        return intl.formatMessage({ id: 'users.error.noUserSelected' });
      }

      const trimmedUsername = values.username.trim();
      const payload: {
        id: string;
        username?: string;
        password?: string;
        role?: typeof values.role;
        tagIds?: string[];
      } = {
        id: selectedUser.id,
      };

      if (trimmedUsername !== selectedUser.username) {
        payload.username = trimmedUsername;
      }
      if (values.role !== selectedUser.role) {
        payload.role = values.role;
      }
      if (values.password) {
        payload.password = values.password;
      }

      const currentTagIds = normalizeTagIds(selectedUser.tags ?? []);
      if (!areTagIdListsEqual(currentTagIds, desiredTagIds)) {
        payload.tagIds = desiredTagIds;
      }

      if (
        payload.username === undefined &&
        payload.role === undefined &&
        payload.password === undefined &&
        payload.tagIds === undefined
      ) {
        resetDialog();
        return;
      }

      await updateUser.mutateAsync(payload);
      resetDialog();
      return;
    } catch (err) {
      if (err instanceof Error) {
        return err.message;
      }
      return intl.formatMessage({ id: 'users.error.failedToSave' });
    }
  };

  if (isLoadingMe) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{intl.formatMessage({ id: 'users.title' })}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-foreground">
            {intl.formatMessage({ id: 'users.loadingAccount' })}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (!me || !isAdminUser) {
    return <AccessDenied />;
  }

  const isSubmitting = dialogMode === 'create' ? createUser.isPending : updateUser.isPending;

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteError(null);
    try {
      await deleteUserMutation.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch (err) {
      if (err instanceof Error) {
        setDeleteError(err.message);
      } else {
        setDeleteError(intl.formatMessage({ id: 'users.failedToDelete' }));
      }
    }
  };

  const isDeleting = deleteUserMutation.isPending;

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{intl.formatMessage({ id: 'users.title' })}</CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetch()}
                disabled={isLoadingUsers || isRefetching}
              >
                {isLoadingUsers || isRefetching
                  ? intl.formatMessage({ id: 'users.refreshing' })
                  : intl.formatMessage({ id: 'users.refresh' })}
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  setDialogMode('create');
                  setSelectedUser(null);
                  setIsDialogOpen(true);
                }}
              >
                {intl.formatMessage({ id: 'users.addUser' })}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {error ? (
            <div className="text-sm text-destructive">
              {intl.formatMessage({ id: 'users.error.failedToLoad' }, { error: error.message })}
            </div>
          ) : isLoadingUsers ? (
            <div className="text-sm text-foreground">
              {intl.formatMessage({ id: 'users.loading' })}
            </div>
          ) : (
            <UsersTable
              users={users}
              currentUserId={me.id}
              onEdit={(user) => {
                setDialogMode('edit');
                setSelectedUser(user);
                setIsDialogOpen(true);
              }}
              onDelete={(user) => {
                setDeleteTarget(user);
                setDeleteError(null);
              }}
            />
          )}
        </CardContent>
      </Card>

      <AddUserDialog
        open={isDialogOpen}
        mode={dialogMode}
        initialValues={dialogInitialValues}
        isSubmitting={isSubmitting}
        onOpenChange={handleDialogOpenChange}
        onSubmit={handleDialogSubmit}
      />

      <Dialog
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTarget(null);
            setDeleteError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{intl.formatMessage({ id: 'users.confirmDelete' })}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-foreground">
            {intl.formatMessage(
              { id: 'users.confirmDeleteDesc' },
              { username: deleteTarget?.username ?? '' },
            )}
          </p>
          {deleteError && <div className="text-sm text-destructive">{deleteError}</div>}
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setDeleteTarget(null);
                setDeleteError(null);
              }}
              disabled={isDeleting}
            >
              {intl.formatMessage({ id: 'users.cancel' })}
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting
                ? intl.formatMessage({ id: 'users.deleting' })
                : intl.formatMessage({ id: 'users.delete' })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
